import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from '../hooks/useStore';
import { hexToOklch } from '../lib/colorUtils';

/**
 * Branding side-effect provider.
 *
 * Watches `db.league` and writes:
 *  - `--brand-arc` (and dependent overlay tokens) on `<html>`
 *  - `document.title`
 *
 * No render output; lives high in the tree so every shell picks up the
 * applied tokens automatically.
 *
 * When `applyToPublic` is false the override is removed so the public
 * marketing surface keeps the default token. Admins still see their custom
 * color via inline styles on the admin shell (added in a later pass if
 * needed).
 */
export function BrandingProvider({ children }: { children: ReactNode }) {
  const league = useStore((d) => d.league);
  const location = useLocation();

  useEffect(() => {
    const root = document.documentElement;
    const oklch = hexToOklch(league.branding.primaryColor);
    const isLeagueHq = location.pathname.startsWith('/admin');
    const isLeaguePublic =
      location.pathname.startsWith('/events') ||
      location.pathname.startsWith('/register') ||
      location.pathname.startsWith('/me');
    const shouldApplyLeagueBrand = isLeagueHq || (isLeaguePublic && league.branding.applyToPublic);

    if (shouldApplyLeagueBrand && oklch) {
      root.style.setProperty('--brand-arc', oklch);
    } else {
      root.style.removeProperty('--brand-arc');
    }
    if (shouldApplyLeagueBrand && league.name) {
      document.title = league.name;
    } else {
      document.title = 'The Scorekeeper';
    }
  }, [league.branding.primaryColor, league.branding.applyToPublic, league.name, location.pathname]);

  return <>{children}</>;
}
